import { List, Map as MapIcon, SplitScreen } from '@carbon/icons-react';
import { ContentSwitcher, Switch } from '@carbon/react';
import type { IconComponent } from '../../types';
import { cx } from '../../utils';
import styles from './CdViewSwitcher.module.scss';

export type ViewMode = 'list' | 'split' | 'map';

const viewMeta: Record<ViewMode, { label: string; icon: IconComponent }> = {
  list: { label: 'List', icon: List },
  split: { label: 'Split', icon: SplitScreen },
  map: { label: 'Map', icon: MapIcon },
};

export interface CdViewSwitcherProps {
  value: ViewMode;
  onChange: (view: ViewMode) => void;
  /** Views to offer – e.g. drop `split` on small screens or `map` when rows have no location. */
  views?: ViewMode[];
  labels?: Partial<Record<ViewMode, string>>;
  className?: string;
}

/** List / Split / Map content switcher from the index page header. */
export const CdViewSwitcher = ({
  value,
  onChange,
  views = ['list', 'split', 'map'],
  labels,
  className,
}: CdViewSwitcherProps) => {
  const selectedIndex = Math.max(0, views.indexOf(value));
  return (
    <ContentSwitcher
      className={cx(styles.switcher, className)}
      size="md"
      lowContrast
      selectedIndex={selectedIndex}
      onChange={({ name }) => {
        if (typeof name === 'string') onChange(name as ViewMode);
      }}
    >
      {views.map((view) => {
        const { label, icon: Icon } = viewMeta[view];
        return (
          <Switch key={view} name={view}>
            <span className={styles.option}>
              {labels?.[view] ?? label}
              <Icon aria-hidden="true" />
            </span>
          </Switch>
        );
      })}
    </ContentSwitcher>
  );
};
